export const AI_MODAL_PANEL_WIDTH = 580;
export const AI_MODAL_GUTTER = 20;

export const AI_MODAL_REFLOW_MEDIA_QUERY = `(width <= ${AI_MODAL_PANEL_WIDTH + AI_MODAL_GUTTER * 2}px)`;

export const AI_FEATURE = {
  has_ai_boolean_creator: {
    name: "Boolean Creator",
  },
  has_ai_content_analysis: {
    name: "Content Analysis",
  },
  has_ai_general_assistant: {
    name: "General Assistant",
  },
  has_ai_keyword_suggestion: {
    name: "Keyword Suggestion",
  },
  has_ai_mention_streams_creator: {
    name: "Mention Stream Creator",
  },
  has_ai_summarisation: {
    name: "Summarisation",
  },
} as const;

export const AI_PROVIDER = {
  google_vertex_ai: {
    name: "Google Vertex AI",
  },
} as const;

export const AI_PROVIDER_MODEL = {
  "gemini-2.0-flash-lite-001": {
    name: "Gemini 2.0 Flash Lite 001",
    provider: "google_vertex_ai",
  },
  "gemini-2.5-flash": {
    name: "Gemini 2.5 Flash",
    provider: "google_vertex_ai",
  },
  "gemini-2.5-flash-lite": {
    name: "Gemini 2.5 Flash Lite",
    provider: "google_vertex_ai",
  },
  "gemini-2.5-flash-preview-05-20": {
    name: "Gemini 2.5 Flash Preview 05-20",
    provider: "google_vertex_ai",
  },
  "gemini-2.5-pro": {
    name: "Gemini 2.5 pro",
    provider: "google_vertex_ai",
  },
  "gemini-2.5-pro-preview-05-06": {
    name: "Gemini 2.5 Pro Preview 05-06",
    provider: "google_vertex_ai",
  },
  "gemini-3-flash-preview": {
    name: "Gemini 3 Flash Preview",
    provider: "google_vertex_ai",
  },
  "gemini-3-pro-preview": {
    name: "Gemini 3 Pro Preview",
    provider: "google_vertex_ai",
  },
  "gemini-3.1-flash-lite": {
    name: "Gemini 3.1 Flash-Lite",
    provider: "google_vertex_ai",
  },
  "gemini-3.1-pro-preview": {
    name: "Gemini 3.1 Pro Preview",
    provider: "google_vertex_ai",
  },
  "gemini-3.5-flash": {
    name: "Gemini 3.5 Flash",
    provider: "google_vertex_ai",
  },
} as const;

export const AI_AGENT_GROUPS = {
  assistant: {
    agents: [
      "assistant_root_agent",
      "assistant_comms",
      "assistant_summarisation",
      "assistant_mention_stream_creator",
      "assistant_mention_stream_strategy",
      "assistant_mention_stream_builder",
      "assistant_boolean_expert",
      "assistant_boolean_basic_writer",
      "assistant_boolean_refinement",
      "assistant_boolean_validator",
      "assistant_boolean_rules_explainer",
      "assistant_boolean_keyword_finder",
      "assistant_boolean_google_search",
    ],
    name: "Assistant Agents",
  },
  content_analysis: {
    agents: [
      "content_analysis_root_agent",
      "content_analysis_comms",
      "content_analysis_general_overview_metrics",
      "content_analysis_mention_stream_builder",
      "content_analysis_sentiment_metrics",
      "content_analysis_boolean_manager",
      "content_analysis_boolean_query_writer",
      "content_analysis_chart_selector",
      "content_analysis_coverage_area_analyser",
      "content_analysis_coverage_volume_metrics",
      "content_analysis_filter_analyser",
      "content_analysis_location_filter",
      "content_analysis_sources_filter",
      "content_analysis_topic_filter",
      "content_analysis_follow_up",
      "content_analysis_google_search",
      "content_analysis_keyword_finder",
      "content_analysis_mention_spike",
      "content_analysis_mentions",
      "content_analysis_metric",
      "content_analysis_potential_audience_reach",
      "content_analysis_suggestions",
      "content_analysis_summarisation",
    ],
    name: "Content Analysis Agents",
  },
  mention_stream: {
    agents: [
      "mention_stream_root_agent",
      "mention_stream_builder",
      "mention_stream_creator_response",
      "mention_stream_strategy",
    ],
    name: "Mention Stream Agents",
  },
  test: {
    agents: ["test_agent_please_ignore"],
    name: "Test Agents",
  },
} as const;

export const AI_AGENT = {
  assistant_boolean_basic_writer: {
    group: "assistant",
    name: "Boolean Basic Writer Agent",
    subAgent: true,
  },
  assistant_boolean_expert: {
    group: "assistant",
    name: "Boolean Expert Agent",
  },
  assistant_boolean_google_search: {
    group: "assistant",
    name: "Boolean Google Search Agent",
    subAgent: true,
  },
  assistant_boolean_keyword_finder: {
    group: "assistant",
    name: "Boolean Keyword Finder Agent",
    subAgent: true,
  },
  assistant_boolean_refinement: {
    group: "assistant",
    name: "Boolean Refinement Agent",
    subAgent: true,
  },
  assistant_boolean_rules_explainer: {
    group: "assistant",
    name: "Boolean Rules Explainer Agent",
    subAgent: true,
  },
  assistant_boolean_validator: {
    group: "assistant",
    name: "Boolean Validator Agent",
    subAgent: true,
  },
  assistant_comms: {
    group: "assistant",
    name: "Comms Agent",
  },
  assistant_mention_stream_builder: {
    group: "assistant",
    name: "Mention Stream Builder Agent",
    subAgent: true,
  },
  assistant_mention_stream_creator: {
    group: "assistant",
    name: "Mention Stream Creator Agent",
  },
  assistant_mention_stream_strategy: {
    group: "assistant",
    name: "Mention Stream Strategy Agent",
    subAgent: true,
  },
  assistant_root_agent: {
    group: "assistant",
    name: "Root Agent",
  },
  assistant_summarisation: {
    group: "assistant",
    name: "Summarisation Agent",
  },
  content_analysis_boolean_manager: {
    group: "content_analysis",
    name: "Boolean Manager",
  },
  content_analysis_boolean_query_writer: {
    group: "content_analysis",
    name: "Boolean Query Writer",
  },
  content_analysis_chart_selector: {
    group: "content_analysis",
    name: "Chart Selector",
  },
  content_analysis_comms: {
    group: "content_analysis",
    name: "Comms Agent",
  },
  content_analysis_coverage_area_analyser: {
    group: "content_analysis",
    name: "Coverage Area Analyser",
  },
  content_analysis_coverage_volume_metrics: {
    group: "content_analysis",
    name: "Coverage Volume Metrics Agent",
  },
  content_analysis_filter_analyser: {
    group: "content_analysis",
    name: "Filter Analyser",
  },
  content_analysis_follow_up: {
    group: "content_analysis",
    name: "Follow Up Agent",
  },
  content_analysis_general_overview_metrics: {
    group: "content_analysis",
    name: "General Overview Metrics Agent",
  },
  content_analysis_google_search: {
    group: "content_analysis",
    name: "Google Search",
  },
  content_analysis_keyword_finder: {
    group: "content_analysis",
    name: "Keyword Finder",
  },
  content_analysis_location_filter: {
    group: "content_analysis",
    name: "Location Filter",
    subAgent: true,
  },
  content_analysis_mention_spike: {
    group: "content_analysis",
    name: "Mention Spike Agent",
  },
  content_analysis_mention_stream_builder: {
    group: "content_analysis",
    name: "Mention Stream Builder Agent",
  },
  content_analysis_mentions: {
    group: "content_analysis",
    name: "Mentions Agent",
  },
  content_analysis_metric: {
    group: "content_analysis",
    name: "Metric Agent",
  },
  content_analysis_potential_audience_reach: {
    group: "content_analysis",
    name: "Potential Audience Reach Agent",
  },
  content_analysis_root_agent: {
    group: "content_analysis",
    name: "Root Agent",
  },
  content_analysis_sentiment_metrics: {
    group: "content_analysis",
    name: "Sentiment Metrics Agent",
  },
  content_analysis_sources_filter: {
    group: "content_analysis",
    name: "Sources Filter",
    subAgent: true,
  },
  content_analysis_suggestions: {
    group: "content_analysis",
    name: "Suggestions Agent",
  },
  content_analysis_summarisation: {
    group: "content_analysis",
    name: "Summarisation Agent",
  },
  content_analysis_topic_filter: {
    group: "content_analysis",
    name: "Topic Filter",
    subAgent: true,
  },
  mention_stream_builder: {
    group: "mention_stream",
    name: "Builder",
  },
  mention_stream_creator_response: {
    group: "mention_stream",
    name: "Creator Response",
  },
  mention_stream_root_agent: {
    group: "mention_stream",
    name: "Root Agent",
  },
  mention_stream_strategy: {
    group: "mention_stream",
    name: "Strategy",
  },
  test_agent_please_ignore: {
    group: "test",
    name: "Test Agent Please Ignore",
  },
} as const;

export const AI_SERVICE_FEATURE = {
  user_context: {
    name: "User Context",
  },
} as const;
